import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import LandingNav from '@/components/landing/LandingNav';
import LandingFooter from '@/components/landing/LandingFooter';

const SUPPORT_EMAIL = 'support@citizenly.net';
const LAST_UPDATED = 'May 12, 2026';
const ease = [0.22, 1, 0.36, 1] as const;

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display font-light text-text-primary text-3xl md:text-4xl leading-tight mb-5">
        {title}
      </h2>
      <div className="font-sans text-base md:text-[17px] text-text-secondary leading-[1.75] space-y-4">
        {children}
      </div>
    </section>
  );
}

function ProviderRow({
  name,
  purpose,
  href,
}: {
  name: string;
  purpose: string;
  href: string;
}) {
  return (
    <li className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 py-3 border-b border-border last:border-b-0">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-sans font-semibold text-text-primary hover:text-primary transition-colors min-w-[140px]"
      >
        {name}
      </a>
      <span className="font-sans text-text-secondary">{purpose}</span>
    </li>
  );
}

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-cream">
      <LandingNav />

      <main className="mx-auto w-full max-w-3xl px-6 md:px-8 pt-16 pb-24 md:pt-24 md:pb-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-10 h-px bg-primary/60" />
          <span className="font-sans text-xs text-text-secondary uppercase tracking-wider">
            Privacy
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05, ease }}
          className="font-display font-light text-text-primary text-5xl md:text-[64px] leading-[1.05] tracking-[-0.01em] mb-5"
        >
          Privacy <span className="italic">Policy</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease }}
          className="font-sans text-sm text-text-muted mb-12"
        >
          Last updated {LAST_UPDATED}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25, ease }}
          className="space-y-14"
        >
          <Section id="intro" title="Who we are">
            <p>
              Citizenly (&ldquo;Citizenly,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo;
              or &ldquo;our&rdquo;) operates citizenly.net, an AI-assisted practice
              tool for the U.S. Citizenship and Immigration Services (USCIS)
              naturalization interview. We are an independent company and are
              <strong className="text-text-primary">
                {' '}not affiliated with USCIS or any U.S. government agency
              </strong>
              .
            </p>
            <p>
              This Privacy Policy explains what information we collect when you
              use the Citizenly service, how we use it, who we share it with, and
              the choices you have. If you have questions, email us at{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section id="information-we-collect" title="Information we collect">
            <p>We collect the following categories of information:</p>
            <ul className="space-y-3 list-none pl-0">
              <li>
                <strong className="text-text-primary">Account information.</strong>{' '}
                Your email address and full name, used to create and secure your
                account.
              </li>
              <li>
                <strong className="text-text-primary">Profile and onboarding.</strong>{' '}
                Information you provide about your upcoming interview, such as
                interview date, English comfort level, and the topics you want to
                focus on.
              </li>
              <li>
                <strong className="text-text-primary">Practice data.</strong>{' '}
                When you complete a civics question or mock interview, we record
                your spoken answers (audio), the transcripts of those answers,
                confidence scores, attempt counts, and time spent.
              </li>
              <li>
                <strong className="text-text-primary">Payment information.</strong>{' '}
                If you subscribe to a paid plan, your payment details (card
                number, billing address) are collected and processed by Stripe.
                We never see or store your full card number. We retain your
                Stripe customer ID and subscription ID so we can manage your
                plan.
              </li>
              <li>
                <strong className="text-text-primary">Usage and device data.</strong>{' '}
                Product analytics events (pages viewed, features used, signup
                steps completed) and basic device data (browser, operating
                system, approximate location from IP address). We also receive
                crash and error reports when something in the app fails.
              </li>
              <li>
                <strong className="text-text-primary">Communications.</strong>{' '}
                If you email us, we keep a record of that conversation so we can
                help you.
              </li>
            </ul>
          </Section>

          <Section id="how-we-use" title="How we use your information">
            <ul className="space-y-3 list-none pl-0">
              <li>To provide the Citizenly service and personalize your practice.</li>
              <li>To grade your spoken answers and surface what to study next.</li>
              <li>To process payments and manage your subscription.</li>
              <li>To send transactional messages (sign-in codes, billing receipts, important account notices).</li>
              <li>To debug, secure, and improve the product.</li>
              <li>To detect and prevent fraud or abuse.</li>
              <li>To comply with legal obligations.</li>
            </ul>
            <p>
              We do <strong className="text-text-primary">not</strong> sell your
              personal information, and we do not share it with advertisers.
            </p>
          </Section>

          <Section id="service-providers" title="Service providers we share with">
            <p>
              To operate Citizenly, we rely on a small number of trusted
              third-party processors. Each one only receives the data they need
              to do their job, and each is bound by their own privacy
              commitments.
            </p>
            <ul className="list-none pl-0 mt-2 mb-0">
              <ProviderRow
                name="Supabase"
                purpose="Authentication and database hosting."
                href="https://supabase.com/privacy"
              />
              <ProviderRow
                name="Stripe"
                purpose="Payments and subscription billing."
                href="https://stripe.com/privacy"
              />
              <ProviderRow
                name="Deepgram"
                purpose="Speech-to-text for your practice answers."
                href="https://deepgram.com/privacy"
              />
              <ProviderRow
                name="PostHog"
                purpose="Product analytics so we can understand usage."
                href="https://posthog.com/privacy"
              />
              <ProviderRow
                name="Sentry"
                purpose="Crash and error monitoring."
                href="https://sentry.io/privacy/"
              />
            </ul>
          </Section>

          <Section id="voice" title="Voice recordings">
            <p>
              When you practice an interview or civics question, we capture a
              short audio clip of your answer and send it to our transcription
              provider (Deepgram) so we can score what you said. Both the audio
              and the transcript are stored against your account so you can
              review your progress later.
            </p>
            <p>
              You can request deletion of your recordings at any time by emailing{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>
              . When you delete your account, your recordings and transcripts
              are deleted with it.
            </p>
          </Section>

          <Section id="cookies" title="Cookies and local storage">
            <p>
              Citizenly uses first-party browser storage (cookies and
              localStorage) to keep you signed in, remember your onboarding
              progress, and run our product analytics. We do not use cookies for
              cross-site advertising and we don&apos;t embed third-party
              advertising trackers.
            </p>
            <p>
              You can clear this storage in your browser settings at any time.
              Doing so will sign you out and reset onboarding state.
            </p>
          </Section>

          <Section id="retention" title="How long we keep your data">
            <p>
              We keep your account information and practice data for as long as
              your account is active. If you delete your account, we delete your
              personal data within 30 days, with two exceptions:
            </p>
            <ul className="space-y-3 list-none pl-0">
              <li>
                <strong className="text-text-primary">Billing records.</strong>{' '}
                We keep invoices and payment records for as long as required by
                tax and accounting law (typically 7 years).
              </li>
              <li>
                <strong className="text-text-primary">Backups.</strong>{' '}
                Encrypted backups may retain your data for a short period after
                deletion before they roll over.
              </li>
            </ul>
          </Section>

          <Section id="rights" title="Your rights and choices">
            <p>
              No matter where you live, you can:
            </p>
            <ul className="space-y-3 list-none pl-0">
              <li>Access the personal information we hold about you.</li>
              <li>Correct information that is wrong or out of date.</li>
              <li>Delete your account and the data associated with it.</li>
              <li>Receive a copy of your data in a portable format.</li>
            </ul>
            <p>
              If you are a California resident, the California Consumer Privacy
              Act (CCPA) gives you the right to know what personal information
              we collect, to delete it, and to opt out of the sale of personal
              information.{' '}
              <strong className="text-text-primary">
                We do not sell personal information
              </strong>
              . We also will not discriminate against you for exercising any of
              these rights.
            </p>
            <p>
              To exercise any of these rights, email{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              from the address on your account. We&apos;ll respond within 30
              days.
            </p>
          </Section>

          <Section id="children" title="Children's privacy">
            <p>
              Citizenly is not directed to children under 13, and we do not
              knowingly collect personal information from anyone under 13. If
              you believe a child has provided us with information, please email{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              and we will delete it.
            </p>
          </Section>

          <Section id="international" title="International users">
            <p>
              Citizenly is operated from the United States and our service
              providers are primarily based in the United States. If you access
              the service from outside the U.S., your information will be
              transferred to, stored in, and processed in the U.S. By using the
              service you consent to that transfer.
            </p>
          </Section>

          <Section id="security" title="Security">
            <p>
              We protect your data in transit with TLS encryption, restrict
              access to production systems on a need-to-know basis, and rely on
              reputable, security-audited infrastructure providers. No system is
              perfectly secure, but we take reasonable steps to keep your data
              safe and to notify you if something goes wrong.
            </p>
          </Section>

          <Section id="changes" title="Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. If we make a
              material change, we&apos;ll let you know by email or in the app.
              The &ldquo;Last updated&rdquo; date at the top of this page always
              reflects the most recent revision.
            </p>
          </Section>

          <Section id="contact" title="Contact us">
            <p>
              Questions about this policy or about your data? Email us at{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>
              . You can also read our{' '}
              <Link
                to="/terms"
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                Terms of Service
              </Link>
              .
            </p>
          </Section>
        </motion.div>
      </main>

      <LandingFooter />
    </div>
  );
}
